import { Controller } from '@hotwired/stimulus';

// The /trendtracker hero: a scroll-snap strip of live comparison charts.
// Every slide is server-rendered and the strip scrolls/swipes on its own, so
// this only adds autoplay, prev/next, the pair pills, arrow keys and the line redraw.
// Autoplay pauses on hover/focus, stops for good once someone swipes or picks
// a slide, and never starts under prefers-reduced-motion.
export default class extends Controller {
  static targets = ['track', 'slide', 'pill', 'pillRow', 'controls'];
  static values = { interval: { type: Number, default: 5500 } };

  connect() {
    this.index = 0;
    this.paused = false;
    this.stopped = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.element.classList.add('is-ready');
    this.controlsTarget.classList.remove('hidden');
    this.controlsTarget.classList.add('flex');
    this.trackTarget.addEventListener('pointerdown', this.stopForGood, { passive: true });
    this.trackTarget.addEventListener('touchstart', this.stopForGood, { passive: true });
    this.trackTarget.addEventListener('wheel', this.onWheel, { passive: true });
    document.addEventListener('visibilitychange', this.onVisibility);
    this.markActive(0, { redraw: false });
    this.schedule();
  }

  disconnect() {
    clearTimeout(this.timer);
    this.trackTarget.removeEventListener('pointerdown', this.stopForGood);
    this.trackTarget.removeEventListener('touchstart', this.stopForGood);
    this.trackTarget.removeEventListener('wheel', this.onWheel);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  prev() {
    this.stopForGood();
    this.goTo(this.index - 1);
  }

  next() {
    this.stopForGood();
    this.goTo(this.index + 1);
  }

  go(event) {
    this.stopForGood();
    this.goTo(Number(event.currentTarget.dataset.index));
  }

  key(event) {
    if (event.key === 'ArrowLeft') this.prev();
    else if (event.key === 'ArrowRight') this.next();
    else return;
    event.preventDefault();
  }

  pause() {
    this.paused = true;
    clearTimeout(this.timer);
  }

  resume(event) {
    // focusout into another element inside the carousel isn't leaving it.
    if (event?.relatedTarget && this.element.contains(event.relatedTarget)) return;
    if (event?.type === 'focusout' && this.element.matches(':hover')) return;
    this.paused = false;
    this.schedule();
  }

  // Swipes and native scrolls land here; work out which slide is now in view.
  scrolled() {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => {
      const width = this.trackTarget.clientWidth || 1;
      const index = Math.round(this.trackTarget.scrollLeft / width);
      // While a button-driven smooth scroll is passing other slides, wait for it to land.
      if (this.landing !== undefined) {
        if (index === this.landing) this.landing = undefined;
        return;
      }
      if (index !== this.index) this.markActive(index);
    });
  }

  goTo(index) {
    const count = this.slideTargets.length;
    const target = (index + count) % count;
    this.landing = target === this.index ? undefined : target;
    this.trackTarget.scrollTo({ left: this.slideTargets[target].offsetLeft - this.trackTarget.offsetLeft });
    // scroll-behavior on the track decides smooth vs instant (instant under reduced motion).
    this.markActive(target);
  }

  markActive(index, { redraw = true } = {}) {
    const changed = index !== this.index;
    this.index = index;
    this.slideTargets.forEach((slide, i) => {
      const active = i === index;
      slide.inert = !active;
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    });
    this.pillTargets.forEach((pill, i) => pill.setAttribute('aria-current', i === index ? 'true' : 'false'));
    this.showPill(this.pillTargets[index]);
    if (redraw && changed) this.redraw(this.slideTargets[index]);
    this.schedule();
  }

  // Scroll only the pill row (never the page) so the active pill sits in the middle.
  showPill(pill) {
    if (!pill || !this.hasPillRowTarget) return;
    const row = this.pillRowTarget;
    row.scrollTo({ left: pill.offsetLeft - (row.clientWidth - pill.offsetWidth) / 2 });
  }

  // Restart the CSS draw-in on the slide that just came into view.
  redraw(slide) {
    slide.querySelectorAll('.tt-line, .tt-area, .tt-end').forEach((el) => {
      el.style.animation = 'none';
      el.getBoundingClientRect();
      el.style.animation = '';
    });
  }

  schedule() {
    clearTimeout(this.timer);
    if (this.stopped || this.paused || document.hidden || this.slideTargets.length < 2) return;
    this.timer = setTimeout(() => this.goTo(this.index + 1), this.intervalValue);
  }

  stopForGood = () => {
    this.stopped = true;
    clearTimeout(this.timer);
  };

  // Horizontal trackpad swipes count as taking over; vertical page scrolls don't.
  onWheel = (event) => {
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) this.stopForGood();
  };

  onVisibility = () => this.schedule();
}
